import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { organizationSaveErrorMessage } from "@/hooks/useOrganizations";
import { languageOptionsFor } from "@/components/corporate/organizations/orgOptions";
import { SaveBar, SectionFrame } from "@/components/corporate/organizations/page/SectionFrame";
import { useReportDirty, useSectionDraft } from "@/components/corporate/organizations/page/useSectionDraft";
import { invalidateOrganization, putOrganization } from "@/components/corporate/organizations/page/orgSave";
import type { Organization } from "@/types/organizations";

interface LanguageSectionProps {
  organization: Organization;
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Settings → Language: English or Arabic (OP-D11). A company already on another stored language
 * shows it "(no longer offered)" and can switch. Saves `defaultLanguage` only.
 */
export function LanguageSection({ organization, onDirtyChange }: LanguageSectionProps) {
  const queryClient = useQueryClient();
  const draft = useSectionDraft({ defaultLanguage: organization.defaultLanguage ?? "" });
  const [saving, setSaving] = useState(false);
  useReportDirty(onDirtyChange, draft.dirty);

  const onSave = async () => {
    if (saving || !draft.dirty) return;
    const defaultLanguage = draft.values.defaultLanguage;
    setSaving(true);
    try {
      await putOrganization(organization.id, { defaultLanguage });
      draft.markSaved(draft.values);
      invalidateOrganization(queryClient, organization.id);
      toast({ title: "Language saved", description: "The company's default language has been updated." });
    } catch (error) {
      toast({
        title: "Error",
        description: organizationSaveErrorMessage(error, "Couldn't save the language. Please try again."),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionFrame
      section="language"
      title="Language"
      hint="The company's default language."
      footer={
        <SaveBar
          dirty={draft.dirty}
          saving={saving}
          onSave={onSave}
          onCancel={draft.discard}
          note="English and Arabic, the same as the create wizard."
        />
      }
    >
      <div className="max-w-sm space-y-1.5">
        <Label htmlFor="org-language">Default language</Label>
        <Select value={draft.values.defaultLanguage} onValueChange={(value) => draft.set("defaultLanguage", value)}>
          <SelectTrigger id="org-language">
            <SelectValue placeholder="Select language" />
          </SelectTrigger>
          <SelectContent>
            {languageOptionsFor(draft.base.defaultLanguage).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </SectionFrame>
  );
}

export default LanguageSection;
